import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface FacebookTokenStatus {
    hasToken: boolean;
    isExpired: boolean;
    expiresAt: string | null;
    hasPostingAccess: boolean;
    selectedPageName: string | null;
    selectedInstagramAccountId: string | null;
}

/**
 * Hook to fetch and cache Facebook token status
 * Returns token status and loading/error states
 */
export const useFacebookTokenStatus = () => {
    const [status, setStatus] = useState<FacebookTokenStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await api.get('/users/facebook-token-status');
            setStatus(response.data);
        } catch (err: unknown) {
            console.error('Failed to fetch Facebook token status:', err);
            setError(getErrorMessage(err));
            setStatus({
                hasToken: false,
                isExpired: false,
                expiresAt: null,
                hasPostingAccess: false,
                selectedPageName: null,
                selectedInstagramAccountId: null,
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        status,
        hasToken: status?.hasToken || false,
        isExpired: status?.isExpired || false,
        expiresAt: status?.expiresAt,
        hasPostingAccess: status?.hasPostingAccess || false,
        selectedPageName: status?.selectedPageName,
        selectedInstagramAccountId: status?.selectedInstagramAccountId,
        isLoading,
        error,
        refetch: fetchStatus,
    };
};
